import React, { useState, useContext, useEffect } from "react";
import { xorDecrypt } from "../encrypt";
import {
  Box,
  Typography,
  ClickAwayListener,
  MenuItem,
  Divider,
  Alert,
  List,
  ListItem,
  Avatar,
  Badge,
  useMediaQuery,
  useTheme,
  Snackbar,
  Grid, // Import Grid component
  Dialog, // Import Dialog component
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button, // Import Button component
} from "@mui/material";
import { fontSize, styled } from "@mui/system";
import {
  Home,
  PersonPin,
  Book,
  Receipt,
  AccountCircle,
  Lock,
  LockOutlined,
  HomeOutlined,
  PersonPinOutlined,
  BookOutlined,
  ReceiptOutlined,
  AccountCircleOutlined,
} from "@mui/icons-material";
import axios from "axios";
import { AuthContext } from "../SessionManagement/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import SessionNotesForm from "../Pages/SessionNote/SessionNotes";
import ProtectedRoute from "../SessionManagement/ProtectedRoutes";
import Dashboard from "../Pages/Dashboard/Dashboard";
import InvoiceTable from "../Pages/Invoice";
import SubscriberDashboard from "../Pages/SubscriberDashboard";
import ManageSessionNote from "../Pages/SessionNote/ViewSessionNote";
import ClientData from "../Client/ClientMain";
import useSharedLink from "../SharedLink";
import MainPage from "../Pages/SessionNote/MainPageSessionNotes/Mainpage";
import axiosInstance from "../api/axiosInstance";
import ManagePassword from "../Pages/ManagePassword";

// Define styled components

const FullPageBox = styled(Box)(({ theme }) => ({
  width: "100vw",
  height: "100vh",
  background: "linear-gradient(to right, #FF5722, #9E9E9E)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxSizing: "border-box",
  position: "relative",
  padding: theme.spacing(2),
}));

const BlackBox = styled(Box)(({ theme }) => ({
  width: "calc(93vw - 10px)", // Adjust width to leave a gap on the right
  height: "calc(92vh - 10px)", // Adjust height to leave a gap at the bottom
  backgroundColor: "#2C2F33",
  borderRadius: "8px",
  padding: theme.spacing(2), // Add padding for better spacing
   // Ensure content is scrollable
   overflow: "auto",
}));

const IconList = styled(List)(({ theme, isMobile }) => ({
  display: "flex",
  flexDirection: isMobile ? "row" : "column",
  justifyContent: isMobile ? "space-around" : "center",
  alignItems: "center",
  padding: "10px",
  gap: theme.spacing(2), // Add gap between icons
  width: "100%",
}));

const ListItemStyled = styled(ListItem)(({ theme, isMobile }) => ({
  display: "flex",
  flexDirection: isMobile ? "row" : "column",
  alignItems: "center",
  gap: theme.spacing(1), // Adjust gap
}));

const IconWrapper = styled(Box)(({ theme, isFilled }) => ({
  width: "60px", // Keep the icon size
  height: "60px", // Keep the icon size
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    marginBottom: "0px",
  },
}));

const iconStyle = (isFilled) => ({
  fontSize: "30px", // Keep the icon size
  color: "white",
  backgroundColor: isFilled
    ? "rgba(255, 255, 255, 0.32)"
    : "rgba(255, 255, 255, 0.1)",
  border: isFilled
    ? "2px solid rgba(255, 255, 255, 0.8)"
    : "2px solid transparent",
  borderRadius: "8px",
  transition: "transform 0.3s ease-in-out, all 0.3s ease-in",
});
const textStyle = {
  color: "white",
  fontSize: "12px", // Adjust font size
  textAlign: "center",
  transition: "transform 0.3s ease-in-out, all 0.3s ease-in",
};

// Define custom styled MenuItem
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: "16px", // Adjust font size
  color: "white",
  padding: theme.spacing(1.5), // Add padding for better spacing
}));

const CustomDropdown = styled(Box)(({ theme }) => ({
  position: "absolute",
  transform: "translate(0, -50%)",
  backgroundColor: "#2C2F33",
  borderRadius: "8px",
  width: "200px",
  zIndex: 1000,
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  marginTop: "-40px",
  border: "1px solid orange",
  padding: theme.spacing(1), // Add padding for better spacing
  [theme.breakpoints.down("md")]: {
    left: "auto",
    right: 0,
    transform: "translate(100%, -50%)",
  },
  [theme.breakpoints.down("sm")]: {
    left: "50%",
    transform: "translate(-50%, 0)",
    width: "90%",
  },
}));

const ClickableBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  cursor: "pointer",
  "&:hover": {
    transform: "scale(1.1)", // Scale up on hover
  },
}));

const DashboardTemplate = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { state, dispatch } = useContext(AuthContext);
  const { isAuthenticated, enrollmentStatus } = state;
  const navigate = useNavigate();
  const decryptedUsername = xorDecrypt(localStorage.getItem("username"));
  const avatarLetter = decryptedUsername
    ? decryptedUsername.charAt(0).toUpperCase()
    : "";
  const sharedLink = useSharedLink();
  const [activePage, setActivePage] = useState("home");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false); // State for logout confirmation dialog
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    // Store the original body style
    const originalStyle = window.getComputedStyle(document.body).background;

    // Apply the linear gradient as the main background
    document.body.style.background =
      "radial-gradient(circle farthest-corner at center center, #FC7E00 0%, #130B12 81%)";

    // Revert back to the original style when the component unmounts
    return () => {
      document.body.style.background = originalStyle;
    };
  }, []);

  const [filledIcons, setFilledIcons] = useState({
    home: false,
    personPin: false,
    book: false,
    receipt: false,
    accountCircle: false,
  });

  const [openDropdown, setOpenDropdown] = useState({
    sessionNote: false,
    profile: false,
  });

  const handleIconClick = (icon) => {
    setFilledIcons((prevState) => ({
      home: icon === "home",
      personPin: icon === "personPin",
      book: icon === "book",
      receipt: icon === "receipt",
      accountCircle: icon === "accountCircle",
    }));
    setOpenDropdown({ sessionNote: false, profile: false });
    const updatedIcons = { ...filledIcons };
    Object.keys(updatedIcons).forEach((key) => {
      updatedIcons[key] = false;
    });
    updatedIcons[icon] = true;
    setFilledIcons(updatedIcons);

    // Set active page based on icon clicked
    setActivePage(icon);
  };

  const handleHomeClick = () => {
    navigate("/");
  };

  const handlePlanClick = () => {
    navigate("/subscription");
  };

  const handleSupportClick = () => {
    navigate("/support");
  };

  const handleFeedbackClick = () => {
    navigate("/feedback");
  };

  const renderActivePage = () => {
    switch (activePage) {
      case "home":
        return (
          <ProtectedRoute>
            <SubscriberDashboard />
          </ProtectedRoute>
        );
      case "personPin":
        return <ClientData />;
      case "book":
        return <MainPage />;
      case "receipt":
        return <InvoiceTable />;
      case "viewSN":
        return <ManageSessionNote />;
      case "password":
        return <ManagePassword />;
    }
  };

  const handleSessionNoteClick = () => {
    setOpenDropdown((prevState) => ({
      sessionNote: !prevState.sessionNote,
      profile: false,
    }));
    setFilledIcons((prevState) => ({
      ...prevState,
      home: false,
      personPin: false,
      book: true,
      receipt: false,
      accountCircle: false,
    }));
  };

  const handleProfileClick = () => {
    setOpenDropdown((prevState) => ({
      sessionNote: false,
      profile: !prevState.profile,
    }));
    setFilledIcons((prevState) => ({
      ...prevState,
      home: false,
      personPin: false,
      book: false,
      receipt: false,
      accountCircle: true,
    }));
  };

  const handleClose = () => {
    setOpenDropdown({ sessionNote: false, profile: false });
  };

  const handleLogout = () => {
    dispatch({ type: "LOGOUT" });
    handleClose();
    navigate("/login");
  };

  const handleLogoutConfirmation = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutDialogClose = (confirm) => {
    setLogoutDialogOpen(false);
    if (confirm) {
      handleLogout();
    }
  };

  const handleCustomerPortalClick = async () => {
    handleClose();
    setSnackbarMessage("Redirecting you to the customer portal...");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);

    try {
      const email = xorDecrypt(localStorage.getItem("username"));

      if (!email) {
        throw new Error("Email not found in local storage");
      }

      const response = await axiosInstance.get(
        `${sharedLink}/api/activeEnrollment?email=${email}`,
        {
          withCredentials: true,
        }
      );
      const customerID = response.data.customerId;
      if (!customerID) {
        throw new Error("Customer ID not found in the response");
      }

      // Open a new tab immediately
      const newTab = window.open("", "_blank");

      if (!newTab) {
        setSnackbarOpen(false);
        setSnackbarMessage("Popup blocked. Please allow popups and try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const portalResponse = await axiosInstance.post(
        `${sharedLink}/customer-portal`,
        null,
        {
          withCredentials: true,
          params: {
            customer: customerID,
          },
          responseType: "json",
        }
      );

      // Set the location of the new tab after the response is received
      newTab.location.href = portalResponse.data.redirectUrl;
    } catch (error) {
      console.error("Error occurred:", error);
      setSnackbarOpen(false);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    setSnackbarOpen(false);
  };
  return (
    <ClickAwayListener onClickAway={handleClose} >
      <Grid
        container
        sx={{
          display: "flex",
          height: "100vh",
          width: "100%",
          padding: theme.spacing(2), // Add padding for better spacing
        }}
      >
        <Grid
          item
          xs={12}
          md={1} // Reduce the width of the menu bar
          sx={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            justifyContent: "space-between", // Adjust layout
            alignItems: "center",
            padding: theme.spacing(4), // Adjust padding
            position: "fixed", // Fix the position
            top: 0,
            left: isMobile ? "0" : "5",
            height: isMobile ? "60px" : "100vh", // Full height for desktop, auto for mobile
            width: isMobile ? "100%" : "5vw", // Full width for mobile, auto for desktop
            backgroundColor: "transparent", // Background color for the menu bar
            zIndex: 1000, // Ensure it stays on top
          }}
        >
          <IconList isMobile={isMobile}>
            <ListItemStyled isMobile={isMobile}>
              <ClickableBox onClick={() => handleIconClick("home")}>
                <IconWrapper>
                  {filledIcons.home ? (
                    <Home sx={iconStyle(filledIcons.home)} />
                  ) : (
                    <HomeOutlined sx={iconStyle(filledIcons.home)} />
                  )}
                </IconWrapper>
                <Typography sx={textStyle}>Dashboard</Typography>
              </ClickableBox>
              <ClickableBox onClick={() => handleIconClick("personPin")}>
                <IconWrapper>
                  {filledIcons.personPin ? (
                    <PersonPin sx={iconStyle(filledIcons.personPin)} />
                  ) : (
                    <PersonPinOutlined sx={iconStyle(filledIcons.personPin)} />
                  )}
                </IconWrapper>
                <Typography sx={textStyle}>Client</Typography>
              </ClickableBox>
              <ClickableBox onClick={handleSessionNoteClick}>
                <IconWrapper>
                  {filledIcons.book ? (
                    <Book sx={iconStyle(filledIcons.book)} />
                  ) : (
                    <BookOutlined sx={iconStyle(filledIcons.book)} />
                  )}
                </IconWrapper>
                <Typography sx={textStyle}>Session Note</Typography>
              </ClickableBox>
              {openDropdown.sessionNote && (
                <ClickAwayListener onClickAway={handleClose}>
                  <CustomDropdown style={{ top: isMobile ? "20vh" : "70%", left: isMobile ? "40vw" : "5vw" }}>
                    <StyledMenuItem onClick={() => handleIconClick("book")}>
                      Add Session Note
                    </StyledMenuItem>
                    <Divider sx={{ bgcolor: "white" }} />
                    <StyledMenuItem onClick={() => handleIconClick("viewSN")}>
                      View Session Notes
                    </StyledMenuItem>
                  </CustomDropdown>
                </ClickAwayListener>
              )}
              <ClickableBox onClick={() => handleIconClick("receipt")}>
                <IconWrapper>
                  {filledIcons.receipt ? (
                    <Receipt sx={iconStyle(filledIcons.receipt)} />
                  ) : (
                    <ReceiptOutlined sx={iconStyle(filledIcons.receipt)} />
                  )}
                </IconWrapper>
                <Typography sx={textStyle}>Invoice</Typography>
              </ClickableBox>
              <ClickableBox onClick={() => handleIconClick("password")}>
                <IconWrapper>
                  {activePage === "password" ? (
                    <Lock sx={iconStyle(true)} />
                  ) : (
                    <LockOutlined sx={iconStyle(false)} />
                  )}
                </IconWrapper>
                <Typography sx={textStyle}>Manage Password</Typography>
              </ClickableBox>;
            </ListItemStyled>
          </IconList>
          <ListItemStyled isMobile={isMobile}>
            <ClickAwayListener onClickAway={handleClose}>
              {isAuthenticated ? (
                <>
                  <ClickableBox onClick={handleProfileClick}>
                    <Avatar
                      sx={{
                        cursor: "pointer",
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                      }}
                    >
                      {avatarLetter}
                    </Avatar>
                    <Typography sx={textStyle}>Profile</Typography>
                  </ClickableBox>
                  {openDropdown.profile && (
                    <ClickAwayListener onClickAway={handleClose}>
                      <Box
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: "-10%",
                          transform: "translate(-50%, 0)",
                          backgroundColor: "#2C2F33",
                          borderRadius: "8px",
                          width: "200px",
                          zIndex: 1000,
                          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                          marginTop: "-160px",
                        }}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                      >
                        <CustomDropdown style={{ top: isMobile ? "25vh" : "70%", left: isMobile ? "20%" : "90%" }}>
                          <StyledMenuItem onClick={handleHomeClick}>
                            Home
                          </StyledMenuItem>
                          <Divider sx={{ bgcolor: "white" }} />
                          <StyledMenuItem onClick={handleCustomerPortalClick}>
                            Billing Setup
                          </StyledMenuItem>
                          <Divider sx={{ bgcolor: "white" }} />
                          <StyledMenuItem onClick={handlePlanClick}>
                            Upgrade Plan
                          </StyledMenuItem>
                          <Divider sx={{ bgcolor: "white" }} />
                          <StyledMenuItem onClick={handleFeedbackClick}>
                            Feedback
                          </StyledMenuItem>
                          <Divider sx={{ bgcolor: "white" }} />
                          <StyledMenuItem onClick={handleSupportClick}>
                            Support
                          </StyledMenuItem>
                          <Divider sx={{ bgcolor: "white" }} />
                          <StyledMenuItem onClick={handleLogoutConfirmation}>
                            Logout
                          </StyledMenuItem>
                        </CustomDropdown>
                      </Box>
                    </ClickAwayListener>
                  )}
                </>
              ) : (
                <>
                  <ClickableBox onClick={handleProfileClick}>
                    <IconWrapper
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Badge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              backgroundColor: "green",
                              borderRadius: "50%",
                              border: "2px solid white",
                            }}
                          />
                        }
                      >
                        <Link
                          to="/login"
                          style={{
                            textDecoration: "none",
                            color: "inherit",
                          }}
                        >
                          {filledIcons.accountCircle ? (
                            <AccountCircle
                              sx={iconStyle(filledIcons.accountCircle)}
                            />
                          ) : (
                            <AccountCircleOutlined
                              sx={iconStyle(filledIcons.accountCircle)}
                            />
                          )}
                        </Link>
                      </Badge>
                      <Typography sx={textStyle}>Profile</Typography>
                    </IconWrapper>
                  </ClickableBox>
                </>
              )}
            </ClickAwayListener>
          </ListItemStyled>
        </Grid>
        <Dialog
  open={logoutDialogOpen}
  onClose={() => handleLogoutDialogClose(false)}
  PaperProps={{
    sx: {
      backgroundColor: "#2C2F33", // Dark gray background
      color: "white", // White text color
      borderRadius: "10px", // Smooth edges
      padding: "20px", // Better spacing
    },
  }}
>
  <DialogTitle
  sx={{
    color: "#FF9800", // Orange heading
    textAlign: "center",
    fontWeight: "bold",
  }}>Confirm Logout</DialogTitle>
  <DialogContent>
    <DialogContentText sx={{ color: "white", textAlign: "center" }}>
      Are you sure you want to logout?
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => handleLogoutDialogClose(false)} variant="contained"
      color="secondary"
      sx={{
        backgroundColor: "#616161", // Dark gray button
        "&:hover": { backgroundColor: "#757575" },
      }}>
      Cancel
    </Button>
    <Button onClick={() => handleLogoutDialogClose(true)} variant="contained"
      sx={{
        backgroundColor: "#FF9800", // Orange button
        color: "white",
        "&:hover": { backgroundColor: "#E68900" }, // Darker orange on hover
      }}>
      Logout
    </Button>
  </DialogActions>
</Dialog>

        <Grid
          item
          md={11} // Adjust the main content width
          sx={{
            padding: theme.spacing(2),
            background: "transparent",
            marginLeft: isMobile ? 0 : "5%", // Adjust margin to account for fixed menu bar
            marginTop: isMobile ? "60px" : 0, // Add margin top for mobile view
          }}
        >
          <BlackBox>
            <div >{renderActivePage()}</div>
          </BlackBox>
        </Grid>
      </Grid>
    </ClickAwayListener>
  );
};
// Export the SideBar component
export default DashboardTemplate;
